export const PANEL_TOOLTIP_MIN_WIDTH = 200;
export const PANEL_TOOLTIP_MAX_WIDTH = 650;
export const PANEL_TOOLTIP_MAX_HEIGHT = 500;
export const PANEL_TOOLTIP_HORIZONTAL_SPACING = 2;
export const PANEL_TOOLTIP_PADDING = 8;

export const PIN_TOOLTIP_HELP_TEXT = 'Click chart to pin';
export const UNPIN_TOOLTIP_HELP_TEXT = 'Unpin';

export interface Coordinate {
  x: number;
  y: number;
}

/**
 * Using zrender mousemove coordinates to handle browser inconsistencies.
 * zrX and zrY are undefined when not hovering over a chart canvas.
 * More info: https://github.com/chronosphereio/monorepo/pull/41721#discussion_r1478946420
 */
export interface ZRCoordinate {
  x?: number;
  y?: number;
}

export interface CursorCoordinates {
  page: Coordinate;
  client: Coordinate;
  plotCanvas: ZRCoordinate;
  target?: EventTarget | null;
}

export interface CursorData {
  coords?: CursorCoordinates | null;
  chartWidth?: number;
}

/**
 * Recreated since zrender does not export out relevant mouse event types.
 * More details in: https://github.com/ecomfe/zrender/blob/6.0.0/src/core/types.ts#L44
 */
type ZREventProperties = {
  /** Mouse coordinate x from zrender (ECharts rendering lib). Helpful for handling browser inconsistencies. */
  zrX?: number;
  /** Mouse coordinate y from zrender (ECharts rendering lib). Helpful for handling browser inconsistencies. */
  zrY?: number;
  zrDelta?: number;
  zrEventControl?: 'no_globalout' | 'only_globalout';
  zrByTouch?: boolean;
};

/**
 * ECharts uses zrender lib under the hood, which extends mouse event types.
 * More details in: https://github.com/ecomfe/zrender/blob/6.0.0/src/core/types.ts#L44
 */
export type ZRRawMouseEvent = MouseEvent & ZREventProperties;

/**
 * Check whether mouse event has zrender custom properties.
 * zrX and zrY are helpful for handling browser inconsistencies.
 */
export const isZRMouseEvent = (event: Event): event is ZRRawMouseEvent => {
  return 'zrX' in event && 'zrY' in event;
};

export interface PanelTooltipConfig {
  enabled?: boolean;
  enablePinning?: boolean;
}

export const DEFAULT_PANEL_TOOLTIP_CONFIG: PanelTooltipConfig = {
  enabled: true,
  enablePinning: true,
};

export const PANEL_TOOLTIP_CONFIG_PINNING_DISABLED: PanelTooltipConfig = {
  enabled: true,
  enablePinning: false,
};

/** Highest throttle time that does not cause noticeable lag when moving cursor */
export const PANEL_TOOLTIP_THROTTLE_MS = 150;
